import { useStore } from 'core/store';
import { useRouteHelper } from 'core/hooks/use-route-helper';

/**
 * Resolves the farm product type + state used for policy-type mask lookups (getFarmPolicyMask and
 * friends), correctly on BOTH the offer route (/offer/:id) and the customer-policy route
 * (/customer/:id/policy/:policyId). (#32207)
 *
 * Why this exists: on the policy route the editable Formik form is the *segment* (PolicyDetails),
 * which carries neither `policyType` nor `selectedOption`, and the offer MST store is empty. Farm
 * components that read the product from `offer.selectedOption` (or `values.policyType`) therefore
 * got `undefined`, so getFarmPolicyMask fell back to the permissive full-Farm (F) mask and leaked
 * farm-only UI (Primary farm operations, farm liability labels/coverages, scheduled/unscheduled
 * farm property, ...) onto Home (Z) / Rental (Y) policies. The product + state live on the parent
 * Policy object in the policy store on the policy route, and on the offer store on the offer route
 * (mirrors how offer.jsx resolves the Farm Details tab label from `store.selectedOption`/`state`).
 *
 * This reads the MST store (not Formik), so it is safe inside nested-Formik contexts such as the
 * add-risk / add-location / edit-* modals whose local Formik does not carry the product.
 *
 * `state` here is the policy/offer-level state. Per-location components should still prefer their
 * own location's address state and fall back to this, since a farm can span multiple states.
 *
 * @returns {{ farmPolicyType: string|undefined, state: string|undefined }}
 */
export const useFarmPolicyContext = () => {
  const { inPolicyRoute } = useRouteHelper();
  const store = useStore();

  if (inPolicyRoute) {
    const policyData = store.account?.policies?.policy?.policy;
    return { farmPolicyType: policyData?.policyType, state: policyData?.state };
  }

  return { farmPolicyType: store.offer?.selectedOption, state: store.offer?.state };
};
